<template>
  <v-dialog v-model="show" max-width="600">
    <v-card>
      <v-form ref="form" @submit.prevent="onSubmit">
        <v-card-title class="headline">{{ buttonTxt }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="section.name"
            label="Section Name"
            :rules="[rules.required]"></v-text-field>
          <v-select
            v-model="selectedSpec"
            label="Select"
            :items="judgeSettings?.data"
            item-value="id"
            item-title="name"
            multiple></v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn color="secondary" @click="emit('rubricSectionCancled')">Cancel</v-btn>
          <v-btn type="submit" color="primary">{{ buttonTxt }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  RubricDTO,
  RubricSectionDTO,
  IRubricSectionDTO,
  IPaginationDTO,
  PaginationDTO,
  JudgeSpecialityDTO,
} from '@/dtos';
import { useRubricStore, useUpdateStatusStore } from '@/stores';
import { SNACKSTATUS } from '@/stores/util';
import { rules } from '@/helpers';
import { useSettingsStore } from '@/stores/modules/admin';

const props = defineProps<{
  rubric: RubricDTO;
  show: boolean;
  yearId: string;
  isAdd: boolean;
  section?: IRubricSectionDTO;
}>();
const Rubric = computed(() => props.rubric);
const yearId = computed(() => props.yearId);
const isAdd = computed(() => props.isAdd);
const buttonTxt = computed(() => (isAdd.value ? 'Add Section' : 'Update Section'));
const emit = defineEmits(['snack', 'rubricSectionSaved', 'rubricSectionCancled']);
const show = computed(() => props.show);
const section = ref<IRubricSectionDTO>(new RubricSectionDTO());
const RubricStore = useRubricStore();
const statusStore = useUpdateStatusStore();
const settingsStore = useSettingsStore();
const pagination = ref<IPaginationDTO>(new PaginationDTO());

const { data: judgeSettings } = settingsStore.getAllJudgeSpecialitiesSettings(pagination);

const selectedSpec = computed({
  get: () => section.value.specialities.map((spec) => spec.id),
  set: (ids: []) => {
    section.value.specialities = [];
    for (const id of ids) {
      section.value.specialities.push(
        new JudgeSpecialityDTO(judgeSettings.value?.data.find((spec) => spec.id == id)),
      );
    }
  },
});

watch(
  () => props.section,
  () => {
    if (props.section) {
      section.value = new RubricSectionDTO(props.section);
    } else {
      section.value = new RubricSectionDTO();
    }
  },
  { immediate: true },
);
const onSubmit = () => {
  if (!section.value.rubric || !section.value.rubric.id) {
    section.value.rubric = new RubricDTO();
    section.value.rubric.id = Rubric.value.id;
  }
  if (isAdd.value) {
    RubricStore.createSection.mutateAsync({ section: section.value, yearId: yearId.value });
  } else {
    RubricStore.updateSection.mutateAsync({
      section: section.value,
      yearId: yearId.value,
      RubricId: Rubric.value.id,
    });
  }
};

watch(
  () => statusStore.getStatus(`rubric-section-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `rubric-section-create`;
    emit('snack', 'Section', value, statusStore.getError(key));
    emit('rubricSectionSaved');
  },
);

const key = computed(() => `rubric-section-update-${section.value.id}`);
watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    emit('snack', 'Section', value, statusStore.getError(key.value));
    emit('rubricSectionSaved');
  },
);
</script>
